// Browser entry: the drafting table (grid, ink, rulers), pointer input, the socket, and the toolbar.

import { BOARD_HEIGHT, BOARD_WIDTH, ERASER, MAX_BRUSH, type Stroke } from "../shared/board.ts";
import { generateRoomId, isValidRoomId, type Peer } from "../shared/identity.ts";
import type { ServerMessage } from "../shared/protocol.ts";
import { backoffDelay, ClientSync, randomId } from "./sync.ts";

const INKS: [name: string, color: string][] = [
  ["Ink", "#e6e4df"],
  ["Silver", "#c5c7ce"],
  ["Ash", "#8e9097"],
  ["Graphite", "#55575e"],
  ["Eraser", ERASER],
];
const PENS = [2, 4, 8, 16];
/** The eraser is wider than the pen it replaces. */
const ERASER_SCALE = 3;
const CURSOR_THROTTLE_MS = 40;
const PING_MS = 20_000;
const STALE_MS = 50_000;
/** Points closer than this (board units) are skipped to keep messages small. */
const MIN_POINT_GAP = 1.5;
const RULER = 20;

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const table = $<HTMLElement>("table");
const gridCanvas = $<HTMLCanvasElement>("grid");
const inkCanvas = $<HTMLCanvasElement>("ink");
const rulerCanvas = $<HTMLCanvasElement>("rulers");
const gridCtx = gridCanvas.getContext("2d") as CanvasRenderingContext2D;
const inkCtx = inkCanvas.getContext("2d") as CanvasRenderingContext2D;
const rulerCtx = rulerCanvas.getContext("2d") as CanvasRenderingContext2D;
const cursorsEl = $<HTMLDivElement>("cursors");
const ringEl = $<HTMLDivElement>("pen-ring");
const titleCard = $<HTMLDivElement>("title-card");
const takesEl = $<HTMLElement>("takes");
const statusEl = $<HTMLSpanElement>("status");
const statusText = $<HTMLSpanElement>("status-text");
const presentEl = $<HTMLSpanElement>("present");
const youName = $<HTMLElement>("you-name");
const readoutEl = $<HTMLElement>("readout");
const inksEl = $<HTMLDivElement>("inks");
const pensEl = $<HTMLDivElement>("pens");
const undoBtn = $<HTMLButtonElement>("undo");
const clearBtn = $<HTMLButtonElement>("clear");
const shareBtn = $<HTMLButtonElement>("share");
const toastEl = $<HTMLDivElement>("toast");

// ---- Room and identity ----

const roomId = resolveRoomId();
$<HTMLElement>("room-name").textContent = roomId;
// Per tab, so two tabs are two visitors but a reload keeps your strokes and name.
const owner = sessionValue("wb-owner", () => randomId(16));

const sync = new ClientSync(owner, (msg) => {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
});

const tool = { ink: INKS[0]?.[1] ?? "#e6e4df", pen: 4 };
const strokeSize = () => (tool.ink === ERASER ? Math.min(MAX_BRUSH, tool.pen * ERASER_SCALE) : tool.pen);

// ---- Socket with reconnect ----

let socket: WebSocket | undefined;
let attempt = 0;
let reconnectTimer: number | undefined;
let lastMessageAt = Date.now();

function connect(): void {
  window.clearTimeout(reconnectTimer);
  reconnectTimer = undefined;
  if (socket && socket.readyState <= WebSocket.OPEN) return;
  setStatus("connecting", attempt === 0 ? "Connecting" : "Retrying");

  const params = new URLSearchParams({ cid: owner });
  const name = sessionStorage.getItem("wb-name");
  if (name) params.set("name", name);
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${scheme}://${location.host}/api/rooms/${roomId}/ws?${params}`);
  socket = ws;

  ws.addEventListener("message", (event) => {
    lastMessageAt = Date.now();
    let msg: ServerMessage;
    try {
      msg = JSON.parse(String(event.data)) as ServerMessage;
    } catch {
      return;
    }
    handleServer(msg);
  });

  ws.addEventListener("close", () => {
    if (socket !== ws) return;
    socket = undefined;
    if (sync.online) toast(OFFLINE_NOTE, 4000);
    sync.disconnected();
    renderPeople();
    renderCursors();
    setStatus("offline", offlineText());
    scheduleReconnect();
  });
}

function scheduleReconnect(): void {
  if (reconnectTimer !== undefined) return;
  reconnectTimer = window.setTimeout(connect, backoffDelay(attempt++));
}

function offlineText(): string {
  return sync.pendingCount > 0 ? `Offline · ${sync.pendingCount} queued` : "Offline";
}

const OFFLINE_NOTE = "Offline. Keep drawing; your strokes are sent when the connection returns.";

function handleServer(msg: ServerMessage): void {
  if (sync.receive(msg)) requestInk();

  switch (msg.t) {
    case "welcome":
      attempt = 0;
      sessionStorage.setItem("wb-name", msg.you.name);
      youName.textContent = msg.you.name;
      setStatus("live", "Live");
      renderPeople();
      renderCursors();
      break;
    case "join":
      toast(`${msg.peer.name} joined`);
      renderPeople();
      break;
    case "leave":
      renderPeople();
      renderCursors();
      break;
    case "cursor":
    case "cursor-leave":
      renderCursors();
      break;
    case "clear":
      toast(`${msg.by} cleared the sheet`);
      break;
    default:
      break;
  }
  updateButtons();
}

// Keep the connection honest: ping, and drop a socket that has gone quiet.
window.setInterval(() => {
  if (socket?.readyState !== WebSocket.OPEN) return;
  if (Date.now() - lastMessageAt > STALE_MS) {
    socket.close();
    return;
  }
  socket.send('{"t":"ping"}');
}, PING_MS);

window.addEventListener("online", () => {
  attempt = 0;
  connect();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && !socket) {
    attempt = 0;
    connect();
  }
});

// ---- Geometry: the 1600×1000 sheet, fitted and centered on the table ----

const view = { scale: 1, ox: 0, oy: 0, width: 0, height: 0, dpr: 1 };

function layout(): void {
  view.width = window.innerWidth;
  view.height = window.innerHeight;
  view.dpr = Math.min(window.devicePixelRatio || 1, 2);
  view.scale = Math.min(view.width / BOARD_WIDTH, view.height / BOARD_HEIGHT);
  view.ox = (view.width - BOARD_WIDTH * view.scale) / 2;
  view.oy = (view.height - BOARD_HEIGHT * view.scale) / 2;
  // On a portrait phone the sheet sits under the slate, leaving room below for the title card.
  if (narrow()) view.oy = Math.min(view.oy, 200);
  for (const canvas of [gridCanvas, inkCanvas, rulerCanvas]) {
    canvas.width = Math.round(view.width * view.dpr);
    canvas.height = Math.round(view.height * view.dpr);
  }
  placeTitleCard();
  drawGrid();
  drawInk();
  drawRulers();
  renderCursors();
  updateRing();
}

const narrow = () => view.width <= 760;
const screenX = (x: number) => view.ox + x * view.scale;

/** Inside the sheet's top-left on wide screens; under the sheet on a phone. */
function placeTitleCard(): void {
  if (narrow()) {
    titleCard.style.fontSize = "54px";
    titleCard.style.transform = `translate(${RULER + 14}px, ${screenY(BOARD_HEIGHT) + 36}px)`;
  } else {
    titleCard.style.fontSize = `${Math.round(118 * view.scale)}px`;
    titleCard.style.transform = `translate(${screenX(90)}px, ${screenY(110)}px)`;
  }
}
const screenY = (y: number) => view.oy + y * view.scale;

// ---- Rendering ----

/** Hairline grid across the whole table, aligned to the sheet: minor every 25 units, major every 100. */
function drawGrid(): void {
  const ctx = gridCtx;
  ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
  ctx.clearRect(0, 0, view.width, view.height);
  const step = 25 * view.scale;
  const firstX = Math.floor(-view.ox / step);
  const firstY = Math.floor(-view.oy / step);
  ctx.lineWidth = 1;
  for (let i = firstX; screenX(i * 25) <= view.width; i++) {
    const x = Math.round(screenX(i * 25)) + 0.5;
    ctx.strokeStyle = i % 4 === 0 ? "rgba(255,255,255,0.075)" : "rgba(255,255,255,0.03)";
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, view.height);
    ctx.stroke();
  }
  for (let j = firstY; screenY(j * 25) <= view.height; j++) {
    const y = Math.round(screenY(j * 25)) + 0.5;
    ctx.strokeStyle = j % 4 === 0 ? "rgba(255,255,255,0.075)" : "rgba(255,255,255,0.03)";
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(view.width, y);
    ctx.stroke();
  }
  // Sheet edge and crop marks.
  ctx.strokeStyle = "rgba(255,255,255,0.13)";
  ctx.strokeRect(Math.round(view.ox) + 0.5, Math.round(view.oy) + 0.5, BOARD_WIDTH * view.scale - 1, BOARD_HEIGHT * view.scale - 1);
}

let inkQueued = false;

function requestInk(): void {
  if (inkQueued) return;
  inkQueued = true;
  requestAnimationFrame(() => {
    inkQueued = false;
    drawInk();
  });
}

function drawInk(): void {
  const ctx = inkCtx;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, inkCanvas.width, inkCanvas.height);
  const s = view.dpr * view.scale;
  ctx.setTransform(s, 0, 0, s, view.ox * view.dpr, view.oy * view.dpr);
  for (const stroke of sync.board.strokes) drawStroke(ctx, stroke);
  ctx.globalCompositeOperation = "source-over";
  titleCard.classList.toggle("faint", sync.board.size > 0 && !narrow());
  takesEl.textContent = String(sync.board.strokes.filter((s) => s.color !== ERASER).length).padStart(3, "0");
  updateButtons();
}

/** Smooth curve through the midpoints of the recorded points. Eraser strokes cut through the ink. */
function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  const p = stroke.points;
  if (p.length < 2) return;
  const erase = stroke.color === ERASER;
  ctx.globalCompositeOperation = erase ? "destination-out" : "source-over";
  ctx.strokeStyle = erase ? "#000" : stroke.color;
  ctx.fillStyle = ctx.strokeStyle;
  ctx.lineWidth = stroke.size;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (p.length === 2) {
    ctx.beginPath();
    ctx.arc(p[0] ?? 0, p[1] ?? 0, stroke.size / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(p[0] ?? 0, p[1] ?? 0);
  for (let i = 2; i < p.length - 2; i += 2) {
    const cx = p[i] ?? 0;
    const cy = p[i + 1] ?? 0;
    ctx.quadraticCurveTo(cx, cy, (cx + (p[i + 2] ?? 0)) / 2, (cy + (p[i + 3] ?? 0)) / 2);
  }
  ctx.lineTo(p[p.length - 2] ?? 0, p[p.length - 1] ?? 0);
  ctx.stroke();
}

let rulersQueued = false;
let ownPointer: { x: number; y: number } | undefined;

function requestRulers(): void {
  if (rulersQueued) return;
  rulersQueued = true;
  requestAnimationFrame(() => {
    rulersQueued = false;
    drawRulers();
  });
}

/**
 * Rulers along the top and left edges, in sheet units. Every visitor's cursor is
 * marked on both rulers, so you can see where everyone is working at a glance.
 */
function drawRulers(): void {
  const ctx = rulerCtx;
  ctx.setTransform(view.dpr, 0, 0, view.dpr, 0, 0);
  ctx.clearRect(0, 0, view.width, view.height);
  ctx.fillStyle = "rgba(8,9,11,0.92)";
  ctx.fillRect(0, 0, view.width, RULER);
  ctx.fillRect(0, RULER, RULER, view.height - RULER);
  ctx.strokeStyle = "rgba(255,255,255,0.13)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(RULER, RULER - 0.5);
  ctx.lineTo(view.width, RULER - 0.5);
  ctx.moveTo(RULER - 0.5, RULER);
  ctx.lineTo(RULER - 0.5, view.height);
  ctx.stroke();

  ctx.font = '8.5px "DM Mono", ui-monospace, monospace';
  ctx.fillStyle = "#6f7178";
  ctx.strokeStyle = "rgba(255,255,255,0.22)";
  ctx.beginPath();
  for (let u = 0; u <= BOARD_WIDTH; u += 25) {
    const x = Math.round(screenX(u)) + 0.5;
    if (x < RULER) continue;
    const major = u % 100 === 0;
    ctx.moveTo(x, RULER);
    ctx.lineTo(x, RULER - (major ? 7 : 3));
    if (u % 200 === 0 && view.scale * 200 > 40) ctx.fillText(String(u), x + 3, 9);
  }
  for (let u = 0; u <= BOARD_HEIGHT; u += 25) {
    const y = Math.round(screenY(u)) + 0.5;
    if (y < RULER) continue;
    const major = u % 100 === 0;
    ctx.moveTo(RULER, y);
    ctx.lineTo(RULER - (major ? 7 : 3), y);
    if (u % 200 === 0 && view.scale * 200 > 40) {
      ctx.save();
      ctx.translate(9, y + 3);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(String(u), -ctx.measureText(String(u)).width, 0);
      ctx.restore();
    }
  }
  ctx.stroke();

  // Markers: white for other visitors, gray for you.
  const marks: [number, number, string][] = [];
  for (const [id, pos] of sync.cursors) if (sync.peers.has(id)) marks.push([pos.x, pos.y, "#e6e4df"]);
  if (ownPointer) marks.push([ownPointer.x, ownPointer.y, "#8e9097"]);
  for (const [bx, by, color] of marks) {
    const x = screenX(bx);
    const y = screenY(by);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x - 4, 0);
    ctx.lineTo(x + 4, 0);
    ctx.lineTo(x, 6);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(0, y - 4);
    ctx.lineTo(0, y + 4);
    ctx.lineTo(6, y);
    ctx.fill();
  }
}

const cursorEls = new Map<string, HTMLDivElement>();

function renderCursors(): void {
  for (const [id, el] of cursorEls) {
    if (!sync.cursors.has(id) || !sync.peers.has(id)) {
      el.remove();
      cursorEls.delete(id);
    }
  }
  for (const [id, pos] of sync.cursors) {
    const peer = sync.peers.get(id);
    if (!peer) continue;
    let el = cursorEls.get(id);
    if (!el) {
      el = document.createElement("div");
      el.className = "cursor";
      el.append(document.createElement("span"));
      (el.firstElementChild as HTMLSpanElement).textContent = peer.name;
      cursorsEl.append(el);
      cursorEls.set(id, el);
    }
    el.style.transform = `translate(${screenX(pos.x)}px, ${screenY(pos.y)}px)`;
  }
  requestRulers();
}

/** "1 · just you" or "3 · Director, Editor", so a new visitor sees who is here without hovering. */
function renderPeople(): void {
  const people: Peer[] = [...(sync.me && sync.online ? [sync.me] : []), ...sync.peers.values()];
  const others = [...sync.peers.values()].map((p) => p.name);
  const list = others.length > 0 ? others.join(", ") : sync.online ? "just you" : "";
  presentEl.textContent = list ? `${people.length} · ${list}` : String(people.length);
  presentEl.title = people.map((p) => p.name).join(", ");
}

function setStatus(state: "connecting" | "live" | "offline", text: string): void {
  statusEl.dataset.state = state;
  statusText.textContent = text;
}

function updateButtons(): void {
  undoBtn.disabled = !sync.board.strokes.some((s) => s.owner === owner);
  clearBtn.disabled = sync.board.size === 0;
}

// ---- Input ----

let activePointer: number | undefined;
let lastPoint: { x: number; y: number } | undefined;
let lastCursorSent = 0;

function toBoard(event: PointerEvent): { x: number; y: number } {
  const x = (event.clientX - view.ox) / view.scale;
  const y = (event.clientY - view.oy) / view.scale;
  return {
    x: Math.round(Math.min(BOARD_WIDTH, Math.max(0, x)) * 10) / 10,
    y: Math.round(Math.min(BOARD_HEIGHT, Math.max(0, y)) * 10) / 10,
  };
}

function onSheet(event: PointerEvent): boolean {
  const x = (event.clientX - view.ox) / view.scale;
  const y = (event.clientY - view.oy) / view.scale;
  return x >= 0 && y >= 0 && x <= BOARD_WIDTH && y <= BOARD_HEIGHT;
}

table.addEventListener("pointerdown", (event) => {
  if (activePointer !== undefined || event.button > 0 || !onSheet(event)) return;
  event.preventDefault();
  activePointer = event.pointerId;
  table.setPointerCapture(event.pointerId);
  const point = toBoard(event);
  lastPoint = point;
  sync.beginStroke(tool.ink, strokeSize(), point.x, point.y);
  requestInk();
});

table.addEventListener("pointermove", (event) => {
  const inside = onSheet(event);
  moveRing(event, inside);
  const point = toBoard(event);
  ownPointer = inside ? point : undefined;
  readoutEl.textContent = inside ? `x ${pad(point.x)}  y ${pad(point.y)}` : "x ----  y ----";
  requestRulers();

  const now = performance.now();
  if (now - lastCursorSent > CURSOR_THROTTLE_MS) {
    lastCursorSent = now;
    if (inside) sync.sendCursor(point.x, point.y);
    else sync.hideCursor();
  }
  if (event.pointerId !== activePointer) return;
  const events = event.getCoalescedEvents?.() ?? [];
  for (const e of events.length > 0 ? events : [event]) {
    const p = toBoard(e);
    if (lastPoint && Math.hypot(p.x - lastPoint.x, p.y - lastPoint.y) < MIN_POINT_GAP) continue;
    lastPoint = p;
    sync.extendStroke(p.x, p.y);
  }
  requestInk();
});

function finishPointer(event: PointerEvent): void {
  if (event.pointerId !== activePointer) return;
  activePointer = undefined;
  lastPoint = undefined;
  sync.endStroke();
  requestInk();
  if (!sync.online) setStatus("offline", offlineText());
  // A finger or pen that lifts is gone; a mouse keeps hovering until it leaves the sheet.
  if (event.pointerType !== "mouse") {
    ownPointer = undefined;
    readoutEl.textContent = "x ----  y ----";
    requestRulers();
    sync.hideCursor();
  }
}

table.addEventListener("pointerup", finishPointer);
table.addEventListener("pointercancel", finishPointer);
table.addEventListener("pointerleave", (event) => {
  if (event.pointerType !== "mouse") return;
  ownPointer = undefined;
  requestRulers();
  sync.hideCursor();
});

// Points go out at most once per frame.
function pump(): void {
  sync.flushPoints();
  requestAnimationFrame(pump);
}
requestAnimationFrame(pump);

function moveRing(event: PointerEvent, inside: boolean): void {
  ringEl.classList.toggle("on", inside);
  ringEl.style.transform = `translate(${event.clientX}px, ${event.clientY}px) translate(-50%, -50%)`;
}

function updateRing(): void {
  const px = Math.max(6, strokeSize() * view.scale);
  ringEl.style.width = `${px}px`;
  ringEl.style.height = `${px}px`;
  ringEl.style.borderStyle = tool.ink === ERASER ? "dashed" : "solid";
}

// ---- Toolbar ----

for (const [index, [name, color]] of INKS.entries()) {
  const button = radio(inksEl, name, `${name} (${index + 1})`, () => selectInk(color));
  button.setAttribute("aria-keyshortcuts", String(index + 1));
  button.classList.add("ink");
  if (color === ERASER) button.classList.add("eraser");
  button.dataset.value = color;
  button.style.setProperty("--ink", color);
  button.append(document.createElement("i"));
}

for (const pen of PENS) {
  const button = radio(pensEl, `Pen ${pen}`, `Pen width ${pen} ([ and ])`, () => selectPen(pen));
  button.setAttribute("aria-keyshortcuts", "BracketLeft BracketRight");
  button.classList.add("pen");
  button.dataset.value = String(pen);
  button.textContent = String(pen).padStart(2, "0");
}

function radio(parent: HTMLElement, label: string, title: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("role", "radio");
  button.setAttribute("aria-label", label);
  button.title = title;
  button.addEventListener("click", onClick);
  parent.append(button);
  return button;
}

function check(parent: HTMLElement, value: string): void {
  for (const el of parent.children) {
    el.setAttribute("aria-checked", String((el as HTMLElement).dataset.value === value));
  }
}

function selectInk(color: string): void {
  tool.ink = color;
  check(inksEl, color);
  updateRing();
}

function selectPen(size: number): void {
  tool.pen = size;
  check(pensEl, String(size));
  updateRing();
}

undoBtn.addEventListener("click", () => {
  sync.undo();
  requestInk();
});
clearBtn.addEventListener("click", () => {
  if (!window.confirm("Clear the sheet for everyone in this room?")) return;
  sync.clear();
  requestInk();
});

let shareLabelTimer: number | undefined;

shareBtn.addEventListener("click", async () => {
  const link = `${location.origin}/r/${roomId}`;
  try {
    await navigator.clipboard.writeText(link);
    shareBtn.textContent = "Copied";
    window.clearTimeout(shareLabelTimer);
    shareLabelTimer = window.setTimeout(() => (shareBtn.textContent = "Copy link"), 2000);
    toast("Link copied. Anyone who opens it draws on this sheet.", 3000);
    return;
  } catch {
    // No clipboard access (older browsers, or a denied permission). Try the share sheet, then a prompt.
  }
  try {
    if (navigator.share) {
      await navigator.share({ title: "Whiteboard", text: `Draw with me on room ${roomId}`, url: link });
      return;
    }
  } catch {
    // The visitor closed the share sheet.
    return;
  }
  window.prompt("Copy this room link:", link);
});

window.addEventListener("keydown", (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
    event.preventDefault();
    sync.undo();
    requestInk();
    return;
  }
  const penIndex = PENS.indexOf(tool.pen);
  if (event.key === "[") selectPen(PENS[Math.max(0, penIndex - 1)] ?? tool.pen);
  else if (event.key === "]") selectPen(PENS[Math.min(PENS.length - 1, penIndex + 1)] ?? tool.pen);
  else if (/^[1-5]$/.test(event.key)) {
    const color = INKS[Number(event.key) - 1]?.[1];
    if (color) selectInk(color);
  }
});

let toastTimer: number | undefined;
function toast(text: string, ms = 2000): void {
  toastEl.textContent = text;
  toastEl.classList.add("show");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove("show"), ms);
}

// ---- Helpers ----

function pad(n: number): string {
  return String(Math.round(n)).padStart(4, "0");
}

function resolveRoomId(): string {
  const match = /^\/r\/([^/]+)\/?$/.exec(location.pathname);
  const fromPath = match ? decodeURIComponent(match[1] ?? "").toLowerCase() : "";
  if (isValidRoomId(fromPath)) {
    // Keep the address bar on the canonical lowercase form so a copied link matches the room.
    if (location.pathname !== `/r/${fromPath}`) history.replaceState(null, "", `/r/${fromPath}${location.search}`);
    return fromPath;
  }
  const fresh = generateRoomId();
  history.replaceState(null, "", `/r/${fresh}${location.search}`);
  if (fromPath) toast("That room link wasn't valid, so this is a new sheet.", 5000);
  return fresh;
}

function sessionValue(key: string, make: () => string): string {
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const value = make();
  sessionStorage.setItem(key, value);
  return value;
}

// ---- Start ----

selectInk(tool.ink);
selectPen(tool.pen);
window.addEventListener("resize", layout);
void document.fonts?.ready.then(drawRulers);
layout();
connect();
